import { IProductDetail } from '@/api/types/public';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { ELensType, EPrescriptionType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Box } from '@mui/material';
import { CheckOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import React, { useContext } from 'react';
import GoodsDetailCollapse from './GoodsDetailCollapse';

// 镜片类型项目组件
const LensTypeItem: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { deviceType } = useContext(ResponsiveContext);
  return (
    <Box className="flex items-center">
      <Box
        className={`flex items-center justify-center ${
          deviceType === EPlatform.WEB
            ? 'w-[20px] h-[20px]'
            : 'w-[14px] h-[14px] rounded-[50%] border border-primary-text mr-8'
        }`}
      >
        <CheckOutlined className={`text-[${deviceType === EPlatform.WEB ? '20px' : '14px'}]`} />
      </Box>
      <Box
        component="span"
        className="ml-2 text-[14px] text-primary-text font-normal leading-[1.3] whitespace-nowrap"
      >
        {children}
      </Box>
    </Box>
  );
};

interface LenseAvailableProps {
  detail: IProductDetail;
  onDetailsClick?: () => void;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

const LenseAvailable: React.FC<LenseAvailableProps> = ({
  detail,
  onDetailsClick,
  SDProductDetailsPageBtnClick,
}) => {
  const t = useTranslations('goodsDetail.lensAvailable');
  const { deviceType } = useContext(ResponsiveContext);
  // 判断是否显示蓝光阻隔
  const showBlueLight = React.useMemo(() => {
    const { lens_type = [] } = detail;
    return (
      Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.BLUE_LIGHT_BLOCKING)
    );
  }, [detail]);

  // 判断是否显示彩色镜片
  const showColorTint = React.useMemo(() => {
    const { lens_type = [] } = detail;
    return Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.COLOR_TINT);
  }, [detail]);

  // 判断是否显示感光变色镜片
  const showPhotochromic = React.useMemo(() => {
    const { lens_type = [] } = detail;
    return (
      Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.PHOTOCHROMIC_LENSES)
    );
  }, [detail]);

  // 判断是否显示驾驶镜片
  const showDriving = React.useMemo(() => {
    const { lens_type = [] } = detail;
    return Array.isArray(lens_type) && lens_type.some((type) => type === ELensType.DRIVING_LENSES);
  }, [detail]);

  // 判断是否显示渐进镜片
  const showProg = React.useMemo(() => {
    const { prescription_type = [] } = detail;
    return (
      Array.isArray(prescription_type) &&
      prescription_type.some((type) => type === ELensType.PROGRESSIVE)
    );
  }, [detail]);

  // 判断是否显示阅读镜片
  const showReading = React.useMemo(() => {
    const { prescription_type = [] } = detail;
    return (
      Array.isArray(prescription_type) &&
      prescription_type.some((type) => type === EPrescriptionType.READING_GLASSES)
    );
  }, [detail]);

  // 判断是否显示此模块
  const showThisModule = React.useMemo(() => {
    const { is_optical_frame, product_lens, is_show_add_to_cart } = detail;

    // 如果配置的加购按钮为add to card，此模块不展示
    if (is_show_add_to_cart === 1) {
      return false;
    }

    // 仅网站商品分类为"镜框/光学镜"的商品，展示此模块
    if (is_optical_frame === 2) {
      return false;
    }

    // 不支持配镜，不展示此模块
    if (product_lens === 2) {
      return false;
    }

    // lens_type包含任何一个或prescription_type包含任何一个就可以展示此模块
    return (
      showBlueLight || showProg || showColorTint || showPhotochromic || showDriving || showReading
    );
  }, [detail, showBlueLight, showProg, showColorTint, showPhotochromic, showDriving, showReading]);

  if (!showThisModule) {
    return null;
  }

  return (
    <GoodsDetailCollapse
      title={t('title')}
      defaultOpen
      onChange={() => {
        onDetailsClick?.();
        SDProductDetailsPageBtnClick?.('Lens Details');
      }}
    >
      <Box
        className={`grid grid-cols-2 gap-y-[10px] lenses-wrap ${
          deviceType === EPlatform.WEB ? 'mb-[24px]' : 'mb-[20px]'
        }`}
      >
        {showProg && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.progressive')}</LensTypeItem>
          </Box>
        )}

        {showReading && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.reading')}</LensTypeItem>
          </Box>
        )}

        {showBlueLight && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.blueLight')}</LensTypeItem>
          </Box>
        )}

        {showColorTint && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.colorTint')}</LensTypeItem>
          </Box>
        )}

        {showPhotochromic && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.photochromic')}</LensTypeItem>
          </Box>
        )}

        {showDriving && (
          <Box className="col-span-1">
            <LensTypeItem>{t('types.driving')}</LensTypeItem>
          </Box>
        )}
      </Box>
    </GoodsDetailCollapse>
  );
};

export default LenseAvailable;
